import { useEffect } from 'react';
import { useContact, useSiteContent } from './SiteContent';

/** Die Stammdaten, die im Markup stehen — ändert der Kunde eine davon, wird nachgezogen. */
const KEYS = [
  'kontaktdaten.firma',
  'kontaktdaten.strasse',
  'kontaktdaten.plz',
  'kontaktdaten.ort',
  'kontaktdaten.telefon',
  'kontaktdaten.email',
];

/**
 * Hält die Firmendaten im `LocalBusiness`-Markup auf dem Stand der Website.
 *
 * Das Markup steht statisch in `public/index.html` — bewusst, damit Crawler ohne
 * ausgeführtes JavaScript die Stammdaten lesen. Sobald der Kunde Telefonnummer
 * oder Adresse ändert, würden die beiden auseinanderlaufen: Auf der Seite stünde
 * die neue Nummer, in den strukturierten Daten die alte. Genau das ist der Fall,
 * in dem Google widersprüchliche Angaben sieht.
 *
 * Deshalb werden die betroffenen Felder hier nachgezogen. Die Koordinaten bleiben
 * unangetastet — eine neue Adresse braucht neue Koordinaten, und die kann niemand
 * aus einem Text ableiten. Ein Umzug bleibt damit eine Änderung am Code.
 */
export function useBusinessJsonLd(): void {
  const contact = useContact();
  const { isChanged } = useSiteContent();
  // Solange niemand etwas geändert hat, steht im Markup ohnehin dasselbe — dann
  // bleibt es unangetastet. So weicht die ausgelieferte Seite im Normalfall auch
  // nicht in einem Zeichen von dem ab, was sie vor der Umstellung war.
  const geaendert = KEYS.some(isChanged);

  useEffect(() => {
    if (!geaendert) return undefined;

    const scripts = Array.from(
      document.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]:not([data-jsonld])'),
    );

    const restores: Array<() => void> = [];

    scripts.forEach((script) => {
      let data: any;
      try {
        data = JSON.parse(script.textContent ?? '');
      } catch {
        return;
      }
      if (data?.['@type'] !== 'LocalBusiness') return;

      const previous = script.textContent;
      restores.push(() => {
        script.textContent = previous;
      });

      script.textContent = JSON.stringify({
        ...data,
        name: contact.company,
        // In internationaler Schreibweise, nicht in der angezeigten: Für
        // strukturierte Daten empfiehlt Google die Nummer mit Ländervorwahl.
        telephone: contact.phoneHref.replace('tel:', ''),
        email: contact.email,
        address: {
          ...data.address,
          streetAddress: contact.street,
          postalCode: contact.zip,
          addressLocality: contact.city,
        },
      });
    });

    return () => restores.forEach((restore) => restore());
  }, [contact, geaendert]);
}

export default useBusinessJsonLd;
